// Terms-of-service dialog. Shown to a signed-in user whose token carries
// an older accepted version than the API's current one. Dismissible:
// Later closes it for this page load, and only account-backed features
// insist through requireTermsAccepted().

import { acceptTerms, fetchTermsInfo, getUser, type TermsInfo } from './auth';
import { needsTermsAcceptance } from './terms';

const DIALOG_ID = 'terms-modal';

/// Version the user dismissed with Later during this page load.
let dismissedVersion: string | null = null;
let openPromise: Promise<boolean> | null = null;

/// True when the signed-in user must accept the current version.
export async function termsPending(): Promise<boolean> {
  const user = getUser();
  if (!user) return false;
  const info = await fetchTermsInfo();
  return needsTermsAcceptance(user.termsVersion, info?.version ?? null);
}

/// Gate for account-backed features. Resolves true when nothing is
/// pending or the user accepts, false when they decline.
export async function requireTermsAccepted(): Promise<boolean> {
  if (!(await termsPending())) return true;
  return openTermsModal();
}

/// Called at boot and on every auth-state change. Prompts once per
/// page load unless the user already pressed Later for this version.
export async function maybePromptTerms(): Promise<void> {
  const user = getUser();
  if (!user) {
    dismissedVersion = null;
    return;
  }
  const info = await fetchTermsInfo();
  if (!info || !needsTermsAcceptance(user.termsVersion, info.version)) return;
  if (dismissedVersion === info.version) return;
  await openTermsModal();
}

/// Open the dialog. Resolves true on a recorded acceptance, false on
/// Later or close. A second call while open returns the same promise.
export function openTermsModal(): Promise<boolean> {
  if (openPromise) return openPromise;
  openPromise = (async () => {
    const info = await fetchTermsInfo();
    if (!info) return false;
    return show(info);
  })().finally(() => {
    openPromise = null;
  });
  return openPromise;
}

function show(info: TermsInfo): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let dialog = document.getElementById(DIALOG_ID) as HTMLDialogElement | null;
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = DIALOG_ID;
      document.body.appendChild(dialog);
    }
    dialog.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'login-title';
    title.textContent = 'Terms of service';
    dialog.appendChild(title);

    const body = document.createElement('div');
    body.className = 'terms-body';
    const intro = document.createElement('p');
    intro.textContent = `Version ${info.version}. Please read and accept the terms of service and the privacy notice to use your account's features.`;
    body.appendChild(intro);

    const links = document.createElement('p');
    links.className = 'terms-links';
    links.appendChild(link(info.termsUrl, 'Terms of service'));
    links.appendChild(document.createTextNode(' · '));
    links.appendChild(link(info.privacyUrl, 'Privacy notice'));
    body.appendChild(links);

    const check = document.createElement('label');
    check.className = 'terms-check';
    const box = document.createElement('input');
    box.type = 'checkbox';
    check.appendChild(box);
    check.appendChild(document.createTextNode(' I have read and accept the terms of service and the privacy notice.'));
    body.appendChild(check);

    const error = document.createElement('div');
    error.className = 'login-error';
    error.hidden = true;
    body.appendChild(error);

    const actions = document.createElement('div');
    actions.className = 'terms-actions';
    const later = document.createElement('button');
    later.type = 'button';
    later.className = 'btn';
    later.textContent = 'Later';
    const accept = document.createElement('button');
    accept.type = 'button';
    accept.className = 'btn btn-primary';
    accept.textContent = 'Accept';
    accept.disabled = true;
    actions.appendChild(later);
    actions.appendChild(accept);
    body.appendChild(actions);
    dialog.appendChild(body);

    let settled = false;
    const finish = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      if (dialog!.open) dialog!.close();
      resolve(ok);
    };

    box.addEventListener('change', () => {
      accept.disabled = !box.checked;
    });
    later.addEventListener('click', () => {
      dismissedVersion = info.version;
      finish(false);
    });
    accept.addEventListener('click', async () => {
      accept.disabled = true;
      later.disabled = true;
      error.hidden = true;
      try {
        await acceptTerms(info.version);
        finish(true);
      } catch (e) {
        if (!getUser()) {
          // The session is gone; the sign-in dialog is the next step,
          // not another Accept. Close without recording a dismissal.
          finish(false);
          return;
        }
        error.textContent = e instanceof Error ? e.message : String(e);
        error.hidden = false;
        accept.disabled = false;
        later.disabled = false;
      }
    });
    // Escape or a backdrop click counts as Later.
    dialog.addEventListener('close', () => {
      if (!settled) dismissedVersion = info.version;
      finish(false);
    });
    dialog.addEventListener('click', (ev) => {
      if (ev.target === dialog) dialog!.close();
    });

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    box.focus();
  });
}

function link(href: string, text: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  a.textContent = text;
  a.target = '_blank';
  a.rel = 'noopener';
  return a;
}
